import { shopifyStorefrontGraphql } from './shopifyStorefront'

import {
  AddCartItemsDocument,
  CartCreateDocument,
  GetCartByIdDocument,
  UpdateCartDocument,
  UpdateCartAttributesDocument,
  type AttributeInput,
  type CartFragmentFragment,
} from '__generated__/graphql'
import { getShopifyVisitorConsent } from 'lib/consent'
import { debugLog } from 'lib/analytics'

type LineItem = { quantity: number; merchandiseId: string; id?: string }

export const getCart = async (
  cartId: string | null
): Promise<CartFragmentFragment | null> => {
  try {
    if (!cartId) return null

    const data = await shopifyStorefrontGraphql(GetCartByIdDocument, {
      id: cartId,
      visitorConsent: getShopifyVisitorConsent(),
    })

    return data?.cart ?? null
  } catch {
    return null
  }
}

const createCart = async (
  lines: Omit<LineItem, 'id'>[]
): Promise<CartFragmentFragment | null> => {
  const data = await shopifyStorefrontGraphql(CartCreateDocument, {
    cartInput: {
      lines,
    },
    visitorConsent: getShopifyVisitorConsent(),
  })

  return data?.cartCreate?.cart ?? null
}

const addCartItem = async (
  cartId: string,
  lines: Omit<LineItem, 'id'>[]
): Promise<CartFragmentFragment | null> => {
  const data = await shopifyStorefrontGraphql(AddCartItemsDocument, {
    cartId,
    lines,
    visitorConsent: getShopifyVisitorConsent(),
  })

  return data?.cartLinesAdd?.cart ?? null
}

const updateCartItem = async (
  cartId: string,
  lines: Required<LineItem>[]
): Promise<CartFragmentFragment | null> => {
  const data = await shopifyStorefrontGraphql(UpdateCartDocument, {
    cartId,
    lines,
    visitorConsent: getShopifyVisitorConsent(),
  })

  return data?.cartLinesUpdate?.cart ?? null
}

export const updateCartAttributes = async (
  cartId: string,
  attributes: AttributeInput[]
): Promise<CartFragmentFragment | null> => {
  // Written right before the checkout redirect; keepalive stops the
  // navigation from cancelling it.
  const data = await shopifyStorefrontGraphql(
    UpdateCartAttributesDocument,
    { cartId, attributes, visitorConsent: getShopifyVisitorConsent() },
    { keepalive: true }
  )

  // Rejections arrive as userErrors on a 200, so surface them in debug mode.
  const userErrors = data?.cartAttributesUpdate?.userErrors ?? []
  if (userErrors.length > 0) {
    debugLog('cart attributes rejected', { userErrors })
  }

  return data?.cartAttributesUpdate?.cart ?? null
}

export const createOrUpdateCart = async (
  cartId: string | undefined,
  lines: LineItem[]
): Promise<CartFragmentFragment | null> => {
  if (!lines.length) return null

  const itemsToCreate = lines
    .filter((item) => !item.id)
    .map(({ id: _id, ...item }) => item)

  const itemsToUpdate = lines.filter(
    (item): item is Required<LineItem> => item.id != null
  )

  if (!cartId) {
    if (itemsToCreate.length > 0) {
      return await createCart(itemsToCreate)
    }
  } else {
    if (itemsToCreate.length) {
      return await addCartItem(cartId, itemsToCreate)
    }

    if (itemsToUpdate.length) {
      return await updateCartItem(cartId, itemsToUpdate)
    }
  }

  return null
}
